import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import api from "api/api";

//Props Validations
import PropTypes from "prop-types";
import isEqual from "lodash/isEqual";

import SkewTabs from "../SkewTabs";
import ViewToggle from "../../ViewToggle";

import { getGraphLabelProductNameWithTestcodes, getViewKeys, getProductScoreWithNaAndRowSpan, getChartProductsData, getChartProductsDataBeautyAndWellbeing, getChartSeries, getDataView, getSortedDataMapping, getAlphabeticalMacroCategories, testResultFlipLabels, getCustomRound } from "../../../../utils/analysisTable/analysisUtils";
import { getTTestResultProductHashMap } from "utils/analysisTable/tTestutils";
import SortFinalData from "../../../../utils/SortFinalData";

// redux
import { loadProductsVisibility } from "../../../../store/features/productsInfoSlice";
import { useSelector, useDispatch } from "react-redux";
import VariableVisibility from "./VariableVisibility";
import GetMacroCategoryOrder from "utils/GetMacroCategoryOrder";
import ScaleAdjuster from "./ScaleAdjuster";
import AnalysisTableBottomLabel from "../AnalysisTableBottomLabel";
import { loadChartScaleResetState } from "store/features/analysisResultsSlice";
import {
    loadOverallAbsoluteToggleState,
    loadSecondaryAbsoluteToggleState,
    loadAgreementAbsoluteToggleState,
    loadClaimsAbsoluteToggleState,
} from "store/features/analysisResultsSlice";

// Inline SVG for tooltip (converted to data URL)
const errorOutlineSvg = encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="red">
        <path d="M11 15h2v2h-2zm0-8h2v6h-2zm1-5C6.48 2 2 6.48 2 12s4.48 
        10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 
        0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 
        8-8 8z"/>
    </svg>
`);

function AnalysisChart({ productsSecondaryData = {}, analysisLevelData = {}, settings = {}, tTest = {} }) {
    const { isITMDataShown = false, moduleName, boxAnalysisTypeSelection, boxAnalysisSelection = {}, analysisTypeValue, mappings = [], penaltyToggle = false } = analysisLevelData;
    const { activeTab, setActiveTab } = settings;
    const { tTestResult, testLabel, tTestLegends, isFisher } = tTest;

    const chartRef = useRef(null);
    const noOfProducts = productsSecondaryData.length;
    const dispatch = useDispatch();

    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const weightToggle = useSelector((state) => state.analysisResultsStore.weightToggle);
    const productsOrder = useSelector((state) => state.productsInfoStore.productsOrder);
    const removedKpiStoreState = useSelector((state) => state.analysisKpiStore[`${moduleName}RemovedKpiList`]);
    const productsTestCodeMapData = useSelector((state) => state.studyDataStore.productTestCodeMap);
    const productColors = useSelector((state) => state.analysisResultsStore.productColors);
    const likertScaleData = useSelector((state) => state.analysisResultsStore.likertScaleData);
    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    const itmToggle = useSelector((state) => state.analysisResultsStore.isItmDataShown);
    const chartScaleReset = useSelector((state) => state.analysisResultsStore.chartScaleReset);
    const absoluteToggle = useSelector((state) => state.analysisResultsStore[`${moduleName}AbsoluteToggle`]);

    const [draggableColumns, setDraggableColumns] = useState(productsSecondaryData);
    const [dataViewKey, setDataViewKey] = useState(""); //ORDER [top:top_weight:mean:weight_mean, bottom:bottom_weight]
    const [box, setBox] = useState(false);

    const [actionStandardKpis, setActionStandardKpis] = useState([]);
    const [asDisplayLabels, setAsDisplayLabels] = useState([]);

    const [variableVisibilityMappings, setVariableVisibilityMappings] = useState([]);

    const customOrderMacroCategory = GetMacroCategoryOrder(productCategoryData?.label?.toLowerCase(), moduleName);

    const [height, setHeight] = useState(230);
    const [yAxisMaxValue, setYAxisMaxValue] = useState(null);

    const [imageHeight, setImageHeight] = useState(300);
    const [inputMinValue, setInputMinValue] = useState(0);
    const [inputMaxValue, setInputMaxValue] = useState(null);
    const [topBottomHasTTestResult, setTopBottomHasTTestResult] = useState(false);
    const [currentZoom, setCurrentZoom] = useState({ start: 0, end: 100 });
    const [testResultFlip, setTestResultFlip] = useState(boxAnalysisTypeSelection.length === 2 ? testResultFlipLabels[0] : boxAnalysisTypeSelection[0]);
    const [tTestLabel, setTTestLabel] = useState("");

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCleansing = process.env?.REACT_APP_SKIN_CLEANSING?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isBeautyAndWellbeing = isHairCare || isSkinCare;
    const isJarSkinCare = isSkinCare && moduleName === "justRight";
    useEffect(() => {
        if (testLabel) {
            if (testLabel.includes("Top & Bottom Box")) {
                setTTestLabel(testLabel);
            } else if (testLabel.includes("Top Box")) {
                setTTestLabel(testLabel.replace("Top Box", testResultFlip));
            } else {
                setTTestLabel(testLabel);
            }
        } else {
            setTTestLabel("");
        }
    }, [testResultFlip, testLabel]);

    useEffect(() => {
        setDraggableColumns(SortFinalData(productsSecondaryData, productsOrder));
    }, [productsSecondaryData, productsOrder]);

    useEffect(() => {
        setCurrentZoom({ start: 0, end: 100 });
        setInputMinValue(0);
    }, [chartScaleReset]);

    useEffect(() => {
        const _dataViewKey = getDataView(analysisTypeValue, weightToggle, boxAnalysisTypeSelection);
        const { value } = analysisTypeValue;
        setBox(value === "box_analysis");
        if (value !== "box_analysis") {
            resetAbsoluteCountToggle()
        }
        setDataViewKey(_dataViewKey);
        if (chartRef.current) {
            const chartInstance = chartRef.current.getEchartsInstance();
            if (chartInstance) {
                // chartInstance.clear();
            }
        }
    }, [analysisTypeValue, boxAnalysisTypeSelection, weightToggle]);

    const getActionStandard = useCallback(() => {
        if (selectedStudy.length === 1) {
            const coreDataId = selectedStudy[0]?.id || null;
            if (coreDataId) {
                const url = process.env.REACT_APP_SERVER_PATH + `/action-standard-groups/submit/kpis/?archive=False&core_data_model=${coreDataId}`;
                try {
                    api.get(url)
                        .then(async (res) => {
                            const response = res.data;
                            const kpis = new Set(
                                response
                                    .map((datum) => {
                                        const _kpi = datum?.action_standard_kpis?.map((item) => item?.kpi || []);

                                        if (isBeautyAndWellbeing || isUnileverFragranceHouse) {
                                            return _kpi.flat().map((bv) => (bv && typeof bv === "object" && "backend_variable" in bv ? bv.backend_variable : bv));
                                        }
                                        return _kpi?.flat() || [];
                                    })
                                    .flat()
                            );

                            setActionStandardKpis([...kpis]);
                        })
                        .catch(async (error) => {
                            console.error(error);
                        });
                } catch (error) {
                    console.error(error);
                }
            }
        }
    }, [selectedStudy]);

    useEffect(() => {
        if (selectedStudy.length === 1 && (moduleName === "overall" || (moduleName === "agreement" && isBeautyAndWellbeing) || (moduleName === "justRight" && (isHairCare || isUnileverFragranceHouse)))) {
            getActionStandard();
        }
    }, [getActionStandard, moduleName, selectedStudy]);

    useEffect(() => {
        if (actionStandardKpis.length > 0) {
            const tempDL = [];
            if (mappings.length > 0) {
                mappings.forEach((datum) => {
                    datum?.displayLabels.forEach((ele) => {
                        if (actionStandardKpis.includes(ele.backendVariable)) {
                            tempDL.push(`${ele.displayLabel} (${likertScaleData[ele.backendVariable]})`);
                        }
                    });
                });
                setAsDisplayLabels([...tempDL]);
            }
        }
    }, [mappings, actionStandardKpis, likertScaleData]);

    const resetAbsoluteCountToggle = () => {
        if (moduleName === "overall") {
            dispatch(loadOverallAbsoluteToggleState(false));
        } else if (moduleName === "secondary") {
            dispatch(loadSecondaryAbsoluteToggleState(false));
        } else if (moduleName === "agreement") {
            dispatch(loadAgreementAbsoluteToggleState(false));
        } else if (moduleName === "claims") {
            dispatch(loadClaimsAbsoluteToggleState(false));
        }
    };

    const getHoverLabel = (bv, key) => {
        return boxAnalysisSelection?.[bv]?.[key]?.label;
    };

    const getFilteredKpis = (activeKpis) => {
        const backendVariableSet = new Set(draggableColumns.flatMap((p) => p.scores.map((s) => s.backendVariable)));
        const filteredKpis = activeKpis.filter((kpi) => backendVariableSet.has(kpi.backendVariable));
        return filteredKpis;
    };

    const checkOverlappingScale = (topLabel, bottomLabel) => {
        const topMatch = topLabel?.match(/\((\d+)(?:([-&])(\d+))?\)/);
        let topArr = [];
        if (topMatch) {
            const start = parseInt(topMatch[1]);
            let end;
            if (topMatch[2]) {
                end = parseInt(topMatch[3]);
            } else {
                end = start;
            }

            for (let i = start; i >= end; i--) {
                topArr.push(i);
            }
        }
        const bottomMatch = bottomLabel?.match(/\((\d+)(?:([-&])(\d+))?\)/);
        let bottomArr = [];
        if (bottomMatch) {
            const start = parseInt(bottomMatch[1]);
            let end;
            if (bottomMatch[2]) {
                end = parseInt(bottomMatch[3]);
            } else {
                end = start;
            }

            for (let i = start; i <= end; i++) {
                bottomArr.push(i);
            }
        }
        const hasCommon = topArr.some((item) => bottomArr.includes(item));

        return hasCommon;
    };

    const getOptionData = useMemo(() => {
        const [key1, key2, key3] = getViewKeys(dataViewKey);
        const isJar = moduleName === "justRight" && (isHairCare || isSkinCare || isUnileverFragranceHouse);
        let catObj = {};
        let macroCategoryOrder = customOrderMacroCategory;
        if (!macroCategoryOrder && moduleName !== "claims") {
            macroCategoryOrder = getAlphabeticalMacroCategories(mappings);
        }
        const sortedData = getSortedDataMapping(mappings, macroCategoryOrder, moduleName, isSkinCleansing || isOralCare || isUnileverFragranceHouse);
        let activeKpis = sortedData.flatMap((datum) => datum.displayLabels);
        if (moduleName === "claims") {
            activeKpis = sortedData.flatMap((datum) => datum.displayLabels);
            catObj = { displayLabels: activeKpis };
        } else if (moduleName !== "overall") {
            const activeMacroCategoryKpis = sortedData.find((ele) => ele.macroCategory === activeTab);
            activeKpis = activeMacroCategoryKpis?.displayLabels || [];
            catObj = activeMacroCategoryKpis || {};
        } else {
            const filteredKpis = getFilteredKpis(activeKpis);
            activeKpis = filteredKpis;
            catObj = { displayLabels: filteredKpis };
        }

        const { productScoreWithNaObj } = getProductScoreWithNaAndRowSpan({ catObj, box, key1, key2, key3, isITMDataShown, moduleName, draggableColumns, noOfProducts, removedKpiStoreState });

        const { _products, _kpis, _rich, _variableVisibilityKpis } = (isBeautyAndWellbeing || isUnileverFragranceHouse)
            ? getChartProductsDataBeautyAndWellbeing(productScoreWithNaObj, activeKpis, key1, key2, key3, draggableColumns, dataViewKey, box, noOfProducts, removedKpiStoreState, tTestResult, likertScaleData, moduleName, isITMDataShown, testResultFlip, itmToggle, isJar, selectedStudy, actionStandardKpis)
            : getChartProductsData(productScoreWithNaObj, activeKpis, key1, key2, draggableColumns, dataViewKey, box, noOfProducts, removedKpiStoreState, tTestResult, likertScaleData, moduleName, isITMDataShown, testResultFlip, itmToggle, absoluteToggle, selectedStudy, actionStandardKpis);

        setVariableVisibilityMappings((prev) => (isEqual(prev, _variableVisibilityKpis) ? prev : _variableVisibilityKpis));

        let tTestProducts = null;
        if ((isBeautyAndWellbeing || isUnileverFragranceHouse) && tTestResult.length > 0) {
            const testName = tTestResult[0]?.test_name?.toLowerCase() || "";
            const isStudentOrZTest = testName.includes("student") || testName.includes("z test");
            const productTitlesInTTest = new Set();
            tTestResult.forEach((item) => {
                item.colors?.forEach((colorObj) => {
                    if (colorObj.productCode) productTitlesInTTest.add(colorObj.productCode);
                });
            });
            tTestProducts = { products: productTitlesInTTest, isStudentOrZTest };
        }

        const { _series, _yAxixMaxValue } = getChartSeries(_products, key1, key2, box, analysisTypeValue, productColors, productColumnVisibility, isJar, tTestProducts, (isBeautyAndWellbeing || isUnileverFragranceHouse));
        let filteredSeries = [..._series];

        if (isJar && productColumnVisibility) {
            filteredSeries = filteredSeries.map((s) => {
                const productTitle = String(s?.name || "").split(" - ")[0];
                const findProduct = draggableColumns.find((p) => p.title === productTitle);
                if (!findProduct) return s;
                const visibilityKey = `checked--${findProduct.title.split(" ").join("_")}__${findProduct.coreDataId}`;
                const isHidden = !!productColumnVisibility?.[visibilityKey];
                return {
                    ...s,
                    silent: isHidden,
                    itemStyle: isHidden ? {
                        ...(s.itemStyle || {}),
                        opacity: 0.9,
                        color: '#cccccc'
                    } : s.itemStyle,
                    data: Array.isArray(s.data) ? s.data.map((d) => (isHidden ? { ...(typeof d === "object" ? d : {}), value: null } : d)) : s.data
                };
            });
        }
        setYAxisMaxValue(_yAxixMaxValue);
        if (analysisTypeValue.value === "mean") {
            setInputMaxValue(((currentZoom?.end / 100) * _yAxixMaxValue).toFixed(2));
        } else {
            setInputMaxValue(Math.round((currentZoom?.end / 100) * _yAxixMaxValue));
        }

        if (tTestResult.length) {
            const checkTTestResult = tTestResult.some(ele => ele?.colors?.some(color => Object.keys(color).includes("ordinary") && color?.ordinary !== null))
            setTopBottomHasTTestResult(checkTTestResult)
        }

        const _option = {
            tooltip: {
                trigger: "item",
                formatter: (params) => {
                    const { seriesName, dataIndex } = params;
                    const product = seriesName.includes("top") ? seriesName.replace(" - top", "") : seriesName.replace(" - bottom", ""); // get "Product A"

                    const marker = (color) => `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:9px;height:9px;background:${color}"></span>`;

                    if (box && _kpis.length) {
                        const { backendVariable, displayLabel } = _kpis[dataIndex];
                        const findProduct = draggableColumns.find((ele) => ele.title === product);
                        const productName = getGraphLabelProductNameWithTestcodes(selectedStudy, findProduct?.study_reference_title, product, productsTestCodeMapData);
                        const bottomSeries = _series.find((s) => s.name === `${product} - bottom`);
                        const topSeries = _series.find((s) => s.name === `${product} - top`);
                        const bottomValue = bottomSeries?.data[dataIndex];
                        const topValue = topSeries?.data[dataIndex];
                        let text = `<span style="font-weight:bold; font-size:12px">${productName}</b><br/>`;
                        if (dataViewKey.includes("top") && dataViewKey.includes("bottom")) {
                            const topLabel = getHoverLabel(backendVariable, "top_box");
                            const bottomLabel = getHoverLabel(backendVariable, "bottom_box");
                            const isOverlappingScale = checkOverlappingScale(topLabel, bottomLabel);
                            if (isOverlappingScale) {
                                text += `<div style="display: flex; align-items: center;">
                                    <div class="warning-icon"><img src="data:image/svg+xml;utf8,${errorOutlineSvg}" width="12" height="12" /></div>
                                    <div style="font-weight:600; font-size:10px; padding-left:5px;">${displayLabel}</div>
                                </div>

                                <div style="font-weight:400;  font-size:8px;"><i>Caution: Overlapping Scales</i></div><br/>`;
                            } else {
                                text += `<span style="font-weight:600; font-size:10px; padding-left:5px;">${displayLabel}</span><br/><br/>`;
                            }
                        } else {
                            text += `<span style="font-weight:600; font-size:10px; padding-left:5px;">${displayLabel}</span><br/><br/>`;
                        }

                        if (dataViewKey.includes("top")) {
                            const topLabel = getHoverLabel(backendVariable, "top_box");
                            text += `${marker(topSeries?.itemStyle?.color)} `;
                            const topDisplay = (topValue === null || topValue === undefined) ? "NA" : (absoluteToggle ? topValue : getCustomRound(Number(topValue)) + "%");
                            text += `${topLabel} <span style="float:right; font-weight:bold; padding-left:20px;">${(isBeautyAndWellbeing || isUnileverFragranceHouse) ? topDisplay : (absoluteToggle ? topValue ? topValue : "NA" : topValue ? getCustomRound(Number(topValue)) + "%" : "NA")}</span><br/>`;
                        }

                        if (dataViewKey.includes("bottom")) {
                            const bottomLabel = getHoverLabel(backendVariable, "bottom_box");
                            text += `${marker(bottomSeries?.itemStyle?.color)} `;
                            const bottomDisplay = (bottomValue === null || bottomValue === undefined) ? "NA" : (absoluteToggle ? bottomValue : getCustomRound(Number(bottomValue)) + "%");
                            text += `${bottomLabel} <span style="float:right; font-weight:bold; padding-left:20px;">${(isBeautyAndWellbeing || isUnileverFragranceHouse) ? bottomDisplay : (absoluteToggle ? bottomValue ? bottomValue : "NA" : bottomValue ? getCustomRound(Number(bottomValue)) + "%" : "NA")}</span><br/>`;
                        }
                        return text;
                    } else if (isJar && _kpis.length) {
                        // JAR tooltip: show hovered product with Too Little / Just Right / Too Much
                        const { displayLabel } = _kpis[dataIndex] || {};
                        // seriesName = "ProductTitle - Too Little/Just Right/Too Much"
                        const productTitle = seriesName.replace(/ - (Too Little|Just Right|Too Much)$/i, "");
                        const findProduct = draggableColumns.find((p) => p.title === productTitle);
                        const productName = getGraphLabelProductNameWithTestcodes(selectedStudy, findProduct?.study_reference_title, productTitle, productsTestCodeMapData);

                        const jarLabels = ["Too Much", "Just Right", "Too Little"];
                        let text = `<span style="font-weight:bold; font-size:12px">${productName}</span><br/>`;
                        text += `<div style="font-weight:400;  font-size:10px;"><i>Caution: Overlapping Scales</i></div><br/>`;
                        text += `<span style="font-weight:600; font-size:10px; color:#555">${displayLabel || ""}</span><br/><br/>`;

                        jarLabels.forEach((jarLabel) => {
                            const jarSeries = _series.find((serie) => serie.name === `${productTitle} - ${jarLabel}`);
                            if (!jarSeries) return;
                            const jarDataItem = jarSeries.data[dataIndex];
                            const jarValue = typeof jarDataItem === 'object' ? jarDataItem.value : jarDataItem;
                            const penalty = typeof jarDataItem === 'object' ? jarDataItem.penalty : null;
                            const valueStr = (jarValue === null || jarValue === undefined) ? 'NA' : `${jarValue}%`;
                            let penaltyStr = '';
                            if (penaltyToggle && (jarLabel === 'Too Little' || jarLabel === 'Too Much')) {
                                const penaltyNum = penalty !== null && penalty !== undefined ? Number(penalty) : null;
                                if (penaltyNum !== null && !isNaN(penaltyNum)) {
                                    penaltyStr = `/ Penalty ${isJarSkinCare ? Math.abs(penaltyNum).toFixed(2) : penaltyNum.toFixed(2)}`;
                                } else {
                                    penaltyStr = `/ Penalty NA`;
                                }
                            }
                            const displayValue = valueStr + penaltyStr;
                            text += `${marker(jarSeries.itemStyle.color)}`;
                            text += `${jarLabel}<span style="float:right; font-weight:bold; padding-left:20px;">${displayValue}</span><br/>`;
                        });

                        return text;
                    } else if (!box && _kpis.length) {
                        // Existing non-box logic
                        const { displayLabel, backendVariable } = _kpis[dataIndex];
                        let testResultProductHashMap = {};
                        if (tTestResult.length) {
                            testResultProductHashMap = getTTestResultProductHashMap(tTestResult);
                        }

                        let text = `<b>${displayLabel}</b><br/>`;
                        _series.forEach((datum) => {
                            const findProduct = draggableColumns.find((ele) => ele.title === datum.name);
                            const productName = getGraphLabelProductNameWithTestcodes(selectedStudy, findProduct?.study_reference_title, datum.name, productsTestCodeMapData);
                            const testResultLabel = testResultProductHashMap?.[backendVariable]?.[datum.name] ?? "";
                            const renderABCLettersHtml = (raw = "", disableColor = false) => {
                                const s = String(raw ?? "").trim();
                                if (!s) return "";
                                const colorMap = {
                                    A: "#A754FF",
                                    B: "#FF8000",
                                    C: "#0084FF",
                                };

                                return s
                                    .split("")
                                    .map((ch) => {
                                        const upper = String(ch).toUpperCase();
                                        const color = colorMap[upper];
                                        if (!color || disableColor) return ch;
                                        return `<span style="color:${color}; font-weight:700;">${ch}</span>`;
                                    })
                                    .join("");
                            };

                            const testResultObj = tTestResult.find((r) => r.backendVariable === backendVariable);
                            const isKruskallWallis = testResultObj?.test_name?.toLowerCase().includes("kruskall wallis");
                            const coloredTestResultLabel = (isBeautyAndWellbeing || isUnileverFragranceHouse) ? renderABCLettersHtml(testResultLabel, isKruskallWallis) : testResultLabel;

                            // marker color for each product
                            text += `${marker(datum.itemStyle.color)} `;
                            const rawVal = datum.data[dataIndex];
                            const displayVal = (rawVal === null || rawVal === undefined) ? "NA" : rawVal;
                            text += `${productName} <span style="float:right; font-weight:bold; padding-left:20px;">${(isBeautyAndWellbeing || isUnileverFragranceHouse) ? displayVal : datum.data[dataIndex]} ${coloredTestResultLabel}</span><br/>`;
                        });

                        return text;
                    }
                    return seriesName;
                },
            },
            legend: {
                show: !["jar analysis", "penalty analysis"].includes(moduleName),
                type: "scroll",
                icon: "roundRect",
                itemGap: 16,
                top: -5,
                left: "center",
                width: "90%",
                height: 50,
                orient: "horizontal",
                // scrollDataIndex: scrollPosition,

                data: (() => {
                    // For JAR analysis, only include "Just Right" entries in the legend
                    if (isJar) {
                        const legendData = [];
                        productsSecondaryData.forEach(product => {
                            // Only add "Just Right" to legend, skip "Too Little" and "Too Much"
                            legendData.push(`${product.title} - Just Right`);
                        });
                        return legendData;
                    }
                    // Beauty & Wellbeing Box Analysis
                    if ((isBeautyAndWellbeing || isUnileverFragranceHouse) && box && key2) {
                        const legendData = [];
                        draggableColumns.map((product) => {
                            legendData.push(`${product.title} - top`);
                        });
                        return legendData;
                    }

                    return undefined;
                })(),
                formatter: function (value) {
                    // Extract JAR category from end: "Product - Too Little" → product="Product", category="Too Little"
                    let product = value.replace(/ - (Too Little|Just Right|Too Much)$/i, '');
                    let jarCategory = '';
                    if (/ - (Too Little|Just Right|Too Much)$/i.test(value)) {
                        jarCategory = value.match(/ - (Too Little|Just Right|Too Much)$/i)[1];
                    } else {
                        product = value.includes("top") ? value.replace(" - top", "") : value.replace(" - bottom", "");
                    }

                    const findProduct = draggableColumns.find((ele) => ele.title === product);
                    const productName = getGraphLabelProductNameWithTestcodes(selectedStudy, findProduct?.study_reference_title, product, productsTestCodeMapData);

                    // JAR legend
                    if (jarCategory && moduleName === "justRight") {
                        if (/^(Too Little|Too Much)$/i.test(jarCategory)) return '';

                        // For "Just Right" remove only the "- Just Right (n=...)" suffix
                        if (/^Just Right$/i.test(jarCategory)) {
                            return `${productName} (n=${findProduct?.sampleSize})`;
                        }
                    }

                    if (jarCategory) {
                        return `${productName} - ${jarCategory} (n=${findProduct?.sampleSize})`;
                    } else if ((isBeautyAndWellbeing || isUnileverFragranceHouse) && box && key2) {
                        return value.includes("top") ? `${productName} (n=${findProduct?.sampleSize})` : null;
                    } else if (box) {
                        const boxProductName = value.includes("top") ? `${productName} - top` : `${productName} - bottom`;
                        return `${boxProductName} (n=${findProduct?.sampleSize})`;
                    } else {
                        return `${productName} (n=${findProduct?.sampleSize})`;
                    }
                    // return value;
                },
                selected: productsSecondaryData.reduce((acc, product) => {
                    const visibilityKey = `checked--${product.title.split(" ").join("_")}__${product.coreDataId}`;
                    const isHidden = !!productColumnVisibility?.[visibilityKey];

                    if (isJar) {
                        // JAR legend only renders "Just Right" entries
                        const justRightKey = `${product.title} - Just Right`;
                        acc[justRightKey] = !isHidden;

                    } else if (box) {
                        const key1Arr = key1.split("_");
                        const title1 = `${product.title} - ${key1Arr[0]}`;
                        if (key2) {
                            const title2 = `${product.title} - bottom`;
                            acc[title2] = !productColumnVisibility[visibilityKey];
                        }
                        acc[title1] = !productColumnVisibility[visibilityKey];
                    } else {
                        acc[product.title] = !productColumnVisibility[visibilityKey];
                    }
                    return acc;
                }, {}),
            },
            grid: {
                show: true,
                containLabel: true,
                tooltip: {
                    show: true,
                },
                left: "10%",
                right: "5%",
                top: "12%",
            },
            xAxis: {
                type: "category",
                data: _kpis.map((ele) => ele.displayLabel),
                axisLabel: {
                    rotate: 90,
                    align: "right",
                    width: 400,
                    padding: [1, 5],
                    fontFamily: "poppinsregular",
                    formatter: function (value, index) {
                        const finaltxt = asDisplayLabels?.includes(value) && selectedStudy.length === 1 ? `{${index}| ${value}*}` : `{${index}| ${value}}`;
                        return finaltxt;
                    },
                    rich: _rich,
                },
            },
            yAxis: {
                type: "value",
                max: _yAxixMaxValue,
            },
            series: filteredSeries,
            dataZoom: isJarSkinCare ? [] : [
                {
                    type: "slider",
                    yAxisIndex: 0,
                    orient: "vertical",
                    width: 15,
                    left: "6%",
                    filterMode: "none",
                    start: currentZoom.start,
                    end: currentZoom.end,
                    rangeMode: ["value", "value"],
                    realtime: false,
                },
                {
                    type: "inside",
                    yAxisIndex: 0,
                    start: currentZoom.start,
                    end: currentZoom.end,
                    rangeMode: ["value", "value"],
                },
            ],
        };
        const _labelMaxLen = _option?.dataset?.source?.[0]?.reduce((max, current) => Math.max(max, current?.length), 0) || 150;
        const _height = 230 + (_labelMaxLen <= 150 ? 150 * 4.15 : _labelMaxLen * 4.15);
        setHeight(_height);

        return _option;
    }, [
        activeTab,
        analysisTypeValue,
        box,
        currentZoom.start,
        currentZoom.end,
        customOrderMacroCategory,
        dataViewKey,
        draggableColumns,
        isBeautyAndWellbeing,
        isUnileverFragranceHouse,
        isITMDataShown,
        isSkinCleansing,
        isOralCare,
        likertScaleData,
        mappings,
        moduleName,
        noOfProducts,
        penaltyToggle,
        productColors,
        productsTestCodeMapData,
        removedKpiStoreState,
        selectedStudy,
        tTestResult,
        testResultFlip,
        productColumnVisibility,
        absoluteToggle,
        asDisplayLabels,
        actionStandardKpis
    ]);

    const handleInputChange = (e, type) => {
        if (type === "max") {
            if (e.target.value <= yAxisMaxValue && e.target.value >= 0) {
                const updatedValue = (parseFloat(e.target.value) * 100) / yAxisMaxValue;
                setInputMaxValue(e.target.value);
                setCurrentZoom({ start: currentZoom.start, end: updatedValue });
            }
        } else if (e.target.value >= 0 && e.target.value <= yAxisMaxValue) {
            const updatedValue = (parseFloat(e.target.value) * 100) / yAxisMaxValue;
            setInputMinValue(e.target.value);
            setCurrentZoom({ start: updatedValue, end: currentZoom.end });
        }
        dispatch(loadChartScaleResetState(true));
    };

    const getOnlyImageHeight = useCallback((height) => {
        setImageHeight(height + 120);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (chartRef.current) {
                const chartInstance = chartRef.current.getEchartsInstance();
                const dom = chartInstance.getDom();

                // The ECharts DOM may not include the outer divs you're selecting
                // Go directly to SVG > g > path
                const pathElement = dom.querySelector("svg > g > path");

                if (pathElement) {
                    const bbox = pathElement.getBBox(); // Get the bounding box of the path element
                    getOnlyImageHeight(bbox.height);
                } else {
                    setImageHeight(height);
                }
            }
        }, 0); // Delay to allow chart to fully render

        // Cleanup the timeout
        return () => clearTimeout(timer);
    }, [activeTab, removedKpiStoreState, box, chartRef, height, getOnlyImageHeight, productsSecondaryData]);

    const handleDataZoom = useCallback(
        (params) => {
            const zoomData = params.batch ? params.batch[0] : params;
            if (zoomData?.start !== undefined && zoomData?.end !== undefined) {
                const newMin = ((zoomData?.start / 100) * yAxisMaxValue).toFixed(2);
                const newMax = ((zoomData?.end / 100) * yAxisMaxValue).toFixed(2);
                if (analysisTypeValue.value === "mean") {
                    setInputMinValue(newMin);
                    setInputMaxValue(newMax);
                    setCurrentZoom({ start: zoomData.start, end: zoomData.end });
                } else {
                    setInputMinValue(Math.round(newMin));
                    setInputMaxValue(Math.round(newMax));
                    setCurrentZoom({ start: Math.round(zoomData.start), end: Math.round(zoomData.end) });
                }
            }
            dispatch(loadChartScaleResetState(true));
        },
        [yAxisMaxValue, analysisTypeValue]
    );

    const updateValuesPosition = () => {
        const textElements = document.querySelectorAll("text");

        textElements.forEach((element) => {
            const value = parseFloat(element.textContent);

            if (value < 30) {
                // Get the current transform matrix as a string
                const matrix = element.getAttribute("transform");

                if (matrix && matrix.startsWith("matrix(")) {
                    // Extract the matrix values
                    const matrixValues = matrix.slice(7, -1).split(",").map(Number);
                    matrixValues[5] -= 44;

                    const newMatrix = `matrix(${matrixValues.join(",")})`;
                    // set the new matrix to transform attribute
                    element.setAttribute("transform", newMatrix);
                    element.style.fill = "#333";
                }
            }
        });
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            updateValuesPosition();
        }, 1500);

        return () => clearTimeout(timeout);
    }, [mappings, currentZoom, inputMaxValue, inputMaxValue, dataViewKey]);

    const handleLegendSelectChanged = (params) => {
        const productTitle = params.name;
        const titleArr = params.name.split(" - ");
        const isHidden = !params.selected[productTitle];
        const index = productsSecondaryData.findIndex((product) => product.title === titleArr[0]);
        if (index !== -1) {
            const product = productsSecondaryData.find((ele) => ele.title === titleArr[0]);
            const visibilityKey = `checked--${product.title.split("/").join("/").trim().split(" ").join("_")}__${product.coreDataId}`;
            // Update the productsVisibility store
            const updatedVisibility = {
                ...productColumnVisibility,
                [visibilityKey]: isHidden,
            };
            dispatch(loadProductsVisibility(updatedVisibility));
        }
    };

    return (
        <div
            style={{
                position: "relative",
                marginBottom: "40px",
            }}
        >
            {!isITMDataShown ? (
                <VariableVisibility
                    mappings={variableVisibilityMappings}
                    moduleName={moduleName}
                />
            ) : (
                <div
                    style={{
                        height: "30px",
                    }}
                ></div>
            )}
            {!isJarSkinCare && (
                <ScaleAdjuster
                    settings={{
                        isBoxAnalysis: box,
                        imageHeight: imageHeight,
                        inputMinValue: inputMinValue,
                        inputMaxValue: inputMaxValue,
                        yAxisMaxValue: yAxisMaxValue,
                        handleInputChange: handleInputChange,
                    }}
                />
            )}
            <div>
                <ReactECharts
                    ref={chartRef}
                    option={getOptionData}
                    style={{
                        minHeight: `${height}px`,
                        height: "100%",
                    }}
                    opts={{ renderer: "svg" }}
                    onEvents={{
                        dataZoom: handleDataZoom,
                        legendSelectChanged: handleLegendSelectChanged,
                    }}
                    notMerge={true}
                    replaceMerge={["series"]}
                />
            </div>
            {box && tTestResult.length !== 0 && topBottomHasTTestResult && (
                <div
                    style={{
                        width: 300,
                        marginBottom: 5,
                        display: "flex",
                        justifyContent: "space-between",
                        border: "1px dotted #005eef",
                        padding: "10px",
                        alignItems: "center",
                    }}
                >
                    <div style={{ width: "150px", fontSize: "13px" }}>Z-Test Result Type:</div>
                    <ViewToggle
                        value={testResultFlip}
                        setValue={setTestResultFlip}
                        labels={testResultFlipLabels}
                        booleanComponent={false}
                    />
                </div>
            )}
            {!["overall", "claims"].includes(moduleName) && (
                <SkewTabs
                    tableData={{
                        productsSecondaryDataState: productsSecondaryData,
                    }}
                    settings={{
                        activeTab: activeTab,
                        setActiveTab: setActiveTab,
                    }}
                    analysisView={{
                        moduleName: moduleName,
                        mappings: mappings,
                        analysisTypeValue: analysisTypeValue,
                        boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                    }}
                />
            )}
            <p
                style={{
                    color: "#495057",
                    fontWeight: 600,
                    fontSize: 12,
                    margin: 0,
                    textAlign: "right",
                }}
            >
                {moduleName === "justRight" ? "5 point Middle-Box Likert scale" : ["claims", "agreement"].includes(moduleName) ? "Agreement Likert scale size is indicated in brackets () next to each variable" : "Likert scale size is indicated in brackets () next to each variable"}
            </p>
            <AnalysisTableBottomLabel
                tTest={{
                    tTestLegends: tTestLegends,
                    isFisher: isFisher,
                    tTestResult: tTestResult,
                    testLabel: tTestLabel,
                }}
                analysisView={{
                    moduleName: moduleName,
                }}
            />
        </div>
    );
}

AnalysisChart.propTypes = {
    productsSecondaryData: PropTypes.array,
    analysisLevelData: PropTypes.object,
    settings: PropTypes.object,
    tTest: PropTypes.object,
};

export default AnalysisChart;
